import { isPlainObject } from "lodash";
import { type DeepPartial } from "react-hook-form";

import { coerceContactsToContactArray } from "./contacts-helpers";

type LocalityPatchData = DeepPartial<Locality>;

/**
 * Deep-merges patch values into an existing value. Plain objects are merged
 * recursively so partial nested updates (e.g. `address: { country }`) keep
 * sibling fields. Arrays and non-plain values are taken from the patch as a
 * whole so list-shaped fields (e.g. `contacts`) replace rather than splice-by-index.
 */
function mergePatchValue(existing: unknown, patchValue: unknown): unknown {
  if (patchValue === undefined) {
    return existing;
  }
  if (patchValue === null) {
    return null;
  }
  if (Array.isArray(patchValue)) {
    return patchValue;
  }
  if (isPlainObject(patchValue)) {
    const base = isPlainObject(existing) ? (existing as Record<string, unknown>) : {};
    const result: Record<string, unknown> = { ...base };
    const patchObj = patchValue as Record<string, unknown>;
    for (const key of Object.keys(patchObj)) {
      const patchVal = patchObj[key];
      if (patchVal === undefined) {
        continue;
      }
      result[key] = mergePatchValue(result[key], patchVal);
    }
    return result;
  }
  return patchValue;
}

/**
 * Merges fields from the PATCH request into each location returned by the API.
 * Ensures the Redux store reflects saved values (e.g. `contacts`) when the
 * response omits or partially returns them, without clobbering unrelated nested
 * fields when the patch only updates a subset (e.g. part of `address`).
 */
export function mergeLocationPatchIntoEntities(entities: Locality[], patch: LocalityPatchData): Locality[] {
  const keys = Object.keys(patch) as (keyof LocalityPatchData)[];
  if (keys.length === 0) {
    return entities;
  }

  const patchRecord = patch as Record<string, unknown>;

  return entities.map(entity => {
    const merged = { ...entity } as Record<string, unknown>;
    for (const key of keys) {
      const value = patchRecord[key as string];
      if (value === undefined) {
        continue;
      }
      /* Align with `normalizeLocationContacts`: API may send `contacts: null`; store an array, not null. */
      if (key === "contacts") {
        merged.contacts = coerceContactsToContactArray(value) as Locality["contacts"];
        continue;
      }
      merged[key as string] = mergePatchValue(merged[key as string], value);
    }
    return merged as unknown as Locality;
  });
}
